/**
 * wetv-kernel 初始化
 * 参考 tv-linux 项目的 projectInit.js
 */

import wetvKernel from '@tencent/wetv-kernel';
import { getAppid, setGuid } from './apiUtils';

const { configInstace, user } = wetvKernel;

/**
 * 判断是否是测试环境
 */
function isTestEnv(): boolean {
  if (typeof window === 'undefined') return false;
  const urlParams = new URLSearchParams(window.location.search);
  return !!urlParams.get('debugger') || window.location.hostname.includes('testlinux');
}

/**
 * 获取 Cookie 值
 * 已禁用 Aegis 上报，该函数暂时不使用
 */
// function getCookieAmend(key: string): string {
//   if (typeof document === 'undefined') return '';
//   const cookies = document.cookie.split(';');
//   for (const cookie of cookies) {
//     const [k, v] = cookie.trim().split('=');
//     if (k === key) {
//       return decodeURIComponent(v || '');
//     }
//   }
//   return '';
// }

/**
 * 初始化 wetv-kernel
 */
export function kernelInit(): void {
  try {
    // 初始化时确保 guid 已生成（必须在其他初始化之前）
    setGuid();

    // CGI环境，默认正式环境。test：测试，pro：正式
    configInstace.cgiEnv = isTestEnv() ? 'test' : 'pro';

    const { videoAppid } = getAppid();

    // 设置应用ID
    configInstace.videoAppid = videoAppid;
    const { aegisAppid } = getAppid();
    if (aegisAppid) {
      configInstace.aegisAppid = aegisAppid;
    }

    // 设置平台信息
    configInstace.platform = '8';
    configInstace.proName = 'linux-tv'; // 项目名
    configInstace.langPrefix = 'linux_tv';

    // 离线日志配置
    configInstace.offlineLogCfg = {
      projectId: 'WETV_LINUX_FEEDBACK_LOG', // 第一步无极配置的项目名，用于建indexDB的database
      index: ['vid', 'cid', 'vuid'], // 可选，你的索引, 命名不能使用 _ 连接
      // 可选，info 为你具体的索引值, 这个值最好在 new OfflineLog 的时候就指定, 不要去修改
      info: {
        vuid: user?.uid || '',
      },
      // 可选，用于每次log的通用日志前缀设置
      prefix: () => {
        const now = new Date();
        const time = now.toISOString();
        const page = typeof window !== 'undefined' ? window.location.href || window.location.pathname || '' : '';
        return `[${time}][${page}]`;
      },
    };

    // 进应用后设置aegis上报时对应的uin用户信息
    // 已禁用 Aegis 上报，避免 localStorage 配额超出
    // 使用 try-catch 包装，避免网络错误影响应用启动
    // try {
    //   const uin = getCookieAmend('vuserid') || '';
    //   if (uin && ageisReportor) {
    //     ageisReportor.updateUin?.({
    //       vuserid: uin,
    //       vusession: getCookieAmend('vusession'),
    //     });
    //   }
    // } catch (error) {
    //   // Aegis 上报失败不影响应用启动，只记录警告
    //   console.warn('⚠️ Aegis 上报初始化失败（不影响应用运行）:', error);
    // }

    console.log('✅ wetv-kernel 初始化完成', {
      cgiEnv: configInstace.cgiEnv,
      videoAppid: configInstace.videoAppid,
      platform: configInstace.platform,
      proName: configInstace.proName,
    });
  } catch (error) {
    // 初始化失败时只记录错误，不抛出，避免阻止应用启动
    console.error('❌ wetv-kernel 初始化失败（应用将继续运行）:', error);
    // 在开发环境中，允许应用继续运行
    if (process.env.NODE_ENV === 'production') {
      // 生产环境可以考虑更严格的错误处理
      console.warn('⚠️ 生产环境 wetv-kernel 初始化失败，但应用将继续运行');
    }
  }
}

/**
 * LG webOS 平台初始化
 * 监听 webOSRelaunch 事件，处理应用重新启动时的参数
 */
export function LGInit(): void {
  if (typeof window === 'undefined') return;
  /**
   * 从 webOS 启动参数中提取 contentTarget
   */
  function getParams(e?: CustomEvent): string | null {
    let launchParams =
      e?.detail || window.webOSDev?.launchParams?.() || window.PalmSystem?.launchParams || window.launchParams;
    if (!launchParams) return null;
    launchParams = typeof launchParams === 'string' ? JSON.parse(launchParams) : launchParams;
    const params = launchParams?.contentTarget;
    return params || null;
  }

  /**
   * 处理 webOS 应用重新启动事件
   */
  function handleRelaunch(e: Event): void {
    const params = getParams(e as CustomEvent);
    // 目前只有 LG 才会有这个参数
    if (params) {
      console.log('[LG] webOSRelaunch 事件触发，参数:', params);
      const playIdMatch = typeof params === 'string' ? params.match(/#\/particular\/([^/]+)/) : null;
      const playId = playIdMatch?.[1] ?? '';
      const queryIndex = typeof params === 'string' ? params.indexOf('?') : -1;
      const queryString = queryIndex >= 0 ? params.slice(0, params.indexOf('#')) : '';
      // host 按环境判断
      const host = isTestEnv() ? 'https://testlinux.wetv.vip' : 'https://tv.wetv.vip';
      const targetUrl = playId ? `${host}/playDetail/${playId}${queryString}` : `${host}${params}`;
      // 使用 location.href 跳转（LG webOS 特定需求）
      // 注意：这会导致整个应用重新加载，但这是 LG 平台的要求
      console.log('[LG] 准备跳转到:', targetUrl);
      window.location.href = targetUrl;
      // 解决 location.href 跳转后页面不刷新的问题
      setTimeout(() => {
        location.reload();
      }, 500);
    }
  }
  // 注册 webOSRelaunch 事件监听器
  document.addEventListener('webOSRelaunch', handleRelaunch);
  console.log('✅ LG webOS 平台初始化完成，已注册 webOSRelaunch 监听器');
}

/**
 * 扩展 Window 接口，添加 LG webOS 相关属性
 */
declare global {
  interface Window {
    webOSDev?: {
      launchParams?: () => any;
    };
    PalmSystem?: {
      launchParams?: any;
    };
    launchParams?: any;
  }
}
